import React, { useState} from 'react';
import {
  Table,
  Button,
  Space,
  Input,
  Card,
  Modal,
  message,
  Row,
  Col,
  Typography,
  Tooltip,
  Statistic,
  ConfigProvider
} from 'antd';
import {
  FilterOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { medicineService, Medicine } from '../../services/medicineService';
import MedicineModal from '../../components/medicines/MedicineModal';
import styles from './MedicinesPage.module.css';
import locale from 'antd/locale/vi_VN';

const { Search } = Input;
const { } = Typography;

const MedicinesPage: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedMedicine, setSelectedMedicine] = useState<Medicine | undefined>();
  const [filters, setFilters] = useState({
    manufacturer: ''
  });

  const queryClient = useQueryClient();

  // Query danh sách sản phẩm
  const { data: medicines = [], isLoading } = useQuery(
    ['medicines', searchText],
    () => searchText ? medicineService.searchMedicines(searchText) : medicineService.getMedicines(),
    {
      staleTime: 5 * 60 * 1000, // 5 phút
    }
  );



  // Mutation xóa sản phẩm
  const deleteMutation = useMutation(medicineService.deleteMedicine, {
    onSuccess: () => {
      message.success('Xóa sản phẩm thành công!');
      queryClient.invalidateQueries('medicines');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Có lỗi xảy ra khi xóa sản phẩm');
    }});

  const handleAdd = () => {
    setSelectedMedicine(undefined);
    setModalVisible(true);
  };

  const handleEdit = (medicine: Medicine) => {
    setSelectedMedicine(medicine);
    setModalVisible(true);
  };

  // Hàm handleDelete đã được cập nhật để dùng Modal.confirm
  const handleDelete = (id: number) => {
    Modal.confirm({
      title: 'Bạn có chắc chắn muốn xóa sản phẩm này?',
      content: 'Hành động này không thể hoàn tác.',
      okText: 'Có, Xóa',
      okType: 'danger',
      cancelText: 'Không',
      onOk: () => {
        deleteMutation.mutate(id);
      },
      onCancel: () => {
        console.log('Hủy bỏ thao tác xóa.');
      }});
  };

  const handleModalSuccess = () => {
    setModalVisible(false);
    setSelectedMedicine(undefined);
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  const clearFilters = () => {
    setFilters({ manufacturer: '' });
    setSearchText('');
  };

  // Lọc dữ liệu theo filters
  const filteredMedicines = medicines.filter((medicine: Medicine) => {
    const matchesManufacturer = !filters.manufacturer ||
      medicine.manufacturer.toLowerCase().includes(filters.manufacturer.toLowerCase());

    return matchesManufacturer;
  });

  const getSummaryStats = () => {
    const totalMedicines = filteredMedicines.length;
    const activeMedicines = filteredMedicines.filter((m: Medicine) => m.is_active).length;
    const inactiveMedicines = filteredMedicines.filter((m: Medicine) => !m.is_active).length;
    const totalCategories = new Set(filteredMedicines.map((m: Medicine) => m.category)).size;

    return (
      <div className={styles.statsContainer}>
        <Row gutter={[24, 16]} className={styles.statsRow}>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#3b82f6' }}>
                  <i className="fas fa-pills" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Tổng sản phẩm"
                    value={totalMedicines}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                  />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#10b981' }}>
                  <i className="fas fa-check-circle" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Đang hoạt động"
                    value={activeMedicines}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                  />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#ef4444' }}>
                  <i className="fas fa-times-circle" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Không hoạt động"
                    value={inactiveMedicines}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                  />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#f59e0b' }}>
                  <i className="fas fa-tags" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Danh mục"
                    value={totalCategories}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                  />
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  const columns = [
    {
      title: 'Sản phẩm',
      key: 'medicine_info',
      width: 240,
      fixed: 'left' as const,
      sorter: (a: Medicine, b: Medicine) => a.name.localeCompare(b.name),
      render: (_: any, record: Medicine) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ 
            fontWeight: '600', 
            fontSize: '15px',
            color: '#1f2937',
            lineHeight: '1.2'
          }}>
            {record.name}
          </div>
          <div style={{ 
            fontSize: '12px', 
            color: '#6b7280',
            fontWeight: '400'
          }}>
            Mã: {record.code}
          </div>
          <div style={{ 
            fontSize: '11px', 
            color: '#9ca3af',
            fontWeight: '400'
          }}>
            SĐK: {record.registration_number}
          </div>
        </div>
      )},
    {
      title: 'Thông tin',
      key: 'medicine_details',
      width: 200,
      render: (_: any, record: Medicine) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ 
            fontSize: '12px',
            color: '#374151',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <i className="fas fa-flask" style={{ color: '#6b7280', fontSize: '10px' }} />
            <span style={{ fontWeight: '500' }}>{record.active_ingredient}</span>
          </div>
          <div style={{ 
            fontSize: '12px',
            color: '#374151',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <i className="fas fa-capsules" style={{ color: '#6b7280', fontSize: '10px' }} />
            <span style={{ fontWeight: '500' }}>{record.dosage_form}</span>
          </div>
          <div style={{ 
            fontSize: '12px',
            color: '#374151',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <i className="fas fa-weight" style={{ color: '#6b7280', fontSize: '10px' }} />
            <span style={{ fontWeight: '500' }}>{record.strength}</span>
          </div>
        </div>
      )},
    {
      title: 'Nhà sản xuất',
      dataIndex: 'manufacturer',
      key: 'manufacturer',
      width: 160,
      render: (manufacturer: string) => (
        <div style={{ 
          fontSize: '13px',
          fontWeight: '500',
          color: '#374151'
        }}>
          {manufacturer}
        </div>
      )},
    {
      title: 'Tồn kho',
      key: 'stock_info',
      width: 140,
      render: (_: any, record: Medicine) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '12px',
            color: '#374151'
          }}>
            <span style={{ fontWeight: '500' }}>Tối thiểu:</span>
            <span style={{ fontWeight: '600', color: '#f59e0b' }}>
              {record.min_stock_level} {record.unit}
            </span>
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '12px',
            color: '#374151',
            borderTop: '1px solid #e5e7eb',
            paddingTop: '4px'
          }}>
            <span style={{ fontWeight: '500' }}>Tối đa:</span>
            <span style={{ fontWeight: '600', color: '#059669' }}>
              {record.max_stock_level} {record.unit}
            </span>
          </div>
        </div>
      )},
    {
      title: 'Danh mục',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (category: string) => (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '6px',
          padding: '4px 8px',
          borderRadius: '12px',
          backgroundColor: '#eff6ff',
          color: '#1d4ed8',
          fontSize: '12px',
          fontWeight: '500',
          width: 'fit-content'
        }}>
          {category}
        </div>
      )},
    {
      title: 'Trạng thái',
      key: 'status',
      width: 140,
      render: (_: any, record: Medicine) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px',
            padding: '6px 12px',
            borderRadius: '20px',
            backgroundColor: record.is_active ? '#d1fae5' : '#fee2e2',
            color: record.is_active ? '#059669' : '#dc2626',
            fontSize: '12px',
            fontWeight: '500',
            width: 'fit-content'
          }}>
            {record.is_active ? 'Hoạt động' : 'Không hoạt động'}
          </div>
          {record.exclude_from_revenue && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px',
              padding: '4px 10px',
              borderRadius: '16px',
              backgroundColor: '#fef3c7',
              color: '#d97706',
              fontSize: '11px',
              fontWeight: '500',
              width: 'fit-content'
            }}>
              <i className="fas fa-ban" style={{ fontSize: '9px' }} />
              Không tính doanh thu
            </div>
          )}
        </div>
      )},
    {
      title: 'Thao tác',
      key: 'actions',
      width: 100,
      fixed: 'right' as const,
      render: (_: any, record: Medicine) => (
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <Tooltip title="Sửa sản phẩm">
            <Button
              type="text"
              icon={<i className="fas fa-edit" />}
              onClick={() => handleEdit(record)}
              size="small"
              style={{ 
                color: '#f59e0b',
                border: 'none',
                boxShadow: 'none'
              }}
            />
          </Tooltip>
          <Tooltip title="Xóa sản phẩm">
            <Button
              type="text"
              icon={<i className="fas fa-trash" />}
              onClick={() => handleDelete(record.id)}
              size="small"
              style={{ 
                color: '#dc2626',
                border: 'none',
                boxShadow: 'none'
              }}
            />
          </Tooltip>
        </div>
      )},
  ];

  return (
    <ConfigProvider locale={locale}>
      <div className={styles.container}>
        {getSummaryStats()}

        <Card className={styles.tableCard}>
          <div className={styles.tableHeader}>
            <div className={styles.searchSection}>
            <Search
              placeholder="Tìm kiếm sản phẩm..."
              allowClear
                className={styles.searchInput}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              onSearch={handleSearch}
                size="large"
                style={{ textAlign: 'left' }}
              />
            </div>
            <div className={styles.filterSection}>
              <Space size="middle" wrap>
            <Input
              placeholder="Lọc theo nhà sản xuất"
              allowClear
              value={filters.manufacturer}
              onChange={(e) => handleFilterChange('manufacturer', e.target.value)}
                  style={{ minWidth: 200 }}
                />
                {(filters.manufacturer || searchText) && (
              <Button
                type="link"
                icon={<FilterOutlined />}
                onClick={clearFilters}
                size="small"
              >
                Xóa bộ lọc
              </Button>
                )}
              </Space>
            </div>
            <div className={styles.actionButtons}>
              <Space size="middle">
                <Tooltip title="Làm mới dữ liệu">
                  <Button
                    icon={<i className="fas fa-sync-alt" />}
                    onClick={() => window.location.reload()}
                    className={styles.actionButton}
                  >
                    Làm mới
                  </Button>
                </Tooltip>
                <Tooltip title="Thêm sản phẩm mới">
                  <Button
                    type="primary"
                    icon={<i className="fas fa-plus" />}
                    onClick={handleAdd}
                    className={styles.primaryButton}
                  >
                    Thêm sản phẩm
                  </Button>
                </Tooltip>
              </Space>
            </div>
          </div>

        <Table
          columns={columns}
          dataSource={filteredMedicines}
          rowKey="id"
          loading={isLoading}
            className={styles.medicinesTable}
            pagination={{
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} của ${total} sản phẩm`,
              className: styles.tablePagination,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
          size="middle"
            scroll={{ x: 900 }}
        />
        </Card>

      <MedicineModal
        visible={modalVisible}
        medicine={selectedMedicine}
        onCancel={() => {
          setModalVisible(false);
          setSelectedMedicine(undefined);
        }}
        onSuccess={handleModalSuccess}
      />
    </div>
    </ConfigProvider>
  );
};

export default MedicinesPage;